// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { cn } from '@/lib/utils';

export interface MeterProps {
  value: number;
  target: number;
  /** Value at which the fill reaches amber. */
  warn?: number;
  /** Value at which the fill reaches red. */
  crit?: number;
  /** Explicit band override (solid fill) — used when warn/crit aren't supplied. */
  status?: 'ok' | 'warn';
  className?: string;
}

const clampPct = (n: number): number => Math.max(0, Math.min(100, n));

// Same softened tokens as the sparkline so the bar matches the graph's color.
const GREEN = 'hsl(var(--status-green) / 0.88)';
const AMBER = 'hsl(var(--status-amber) / 0.8)';
const RED = 'hsl(var(--status-red))';

/**
 * Continuous heat color for a value: a single color for the whole bar that
 * blends smoothly green→amber→red as the value rises (it doesn't snap between
 * three discrete bands). Stays green until ~70% of `warn`, ramps to amber at
 * `warn`, then to red at `crit`. Mirrors how the sparkline fades by value.
 */
function heatColor(value: number, warn: number, crit: number): string {
  if (value >= crit) return RED;
  if (value >= warn) {
    const t = Math.round(clampPct(((value - warn) / (crit - warn)) * 100));
    return `color-mix(in oklab, ${AMBER}, ${RED} ${t}%)`;
  }
  const lo = warn * 0.7; // comfortable values stay green
  if (value <= lo) return GREEN;
  const t = Math.round(clampPct(((value - lo) / (warn - lo)) * 100));
  return `color-mix(in oklab, ${GREEN}, ${AMBER} ${t}%)`;
}

export function Meter({
  value,
  target,
  warn,
  crit,
  status,
  className,
}: MeterProps): React.ReactElement {
  const pct = target <= 0 ? 0 : clampPct((value / target) * 100);
  const heat = warn !== undefined && crit !== undefined;

  // Solid fallback when no thresholds are supplied.
  const fallbackClass =
    (status ?? (pct >= 90 ? 'warn' : 'ok')) === 'warn'
      ? 'bg-[hsl(var(--status-amber))]'
      : 'bg-[hsl(var(--status-green))]';

  // Continuous color via a custom property so it can use color-mix and animate
  // smoothly between updates (transition on background-color below).
  const fillStyle = { width: `${pct}%` } as React.CSSProperties & Record<string, string>;
  if (heat) fillStyle['--meter-fill'] = heatColor(value, warn, crit);

  return (
    <div
      className={cn(
        'h-1 w-full rounded-full bg-[hsl(var(--foreground)/0.08)] overflow-hidden',
        className,
      )}
      aria-hidden="true"
    >
      <span
        className={cn(
          'block h-full rounded-full transition-[width,background-color] duration-700',
          heat ? 'bg-[var(--meter-fill)]' : fallbackClass,
        )}
        style={fillStyle}
      />
    </div>
  );
}
